import { logger } from 'logging';

// Error messages
const ERROR_FOR_CAPTCHA_NOT_LOADED = 'reCaptcha script not loaded';
const ERROR_FOR_CAPTCHA_EXECUTION = 'reCaptcha execution error';
const CAPTCHA_LOADED = 'reCaptcha script loaded';

// Interface for optional metadata
interface MetaForCaptcha {
  location?: string;
}

// Recaptcha API key from environment variable
const API_KEY = process.env.G_RECAPTCHA_V3;

/**
 * Creates the reCAPTCHA script element.
 * @returns The script element for loading reCAPTCHA
 */
function createCaptchaScript(): HTMLScriptElement {
  const recaptchaV3Script = document.createElement('script');
  recaptchaV3Script.type = 'text/javascript';
  recaptchaV3Script.id = 'google_recaptcha_v3_api';
  recaptchaV3Script.src = `https://www.google.com/recaptcha/api.js?render=${API_KEY}`;
  return recaptchaV3Script;
}

/**
 * Handles the load event for the reCAPTCHA script.
 * @param resolve - The promise resolve function
 * @param reject - The promise reject function
 */
function handleScriptLoad(
  resolve: (value: string) => void,
  reject: (reason?: any) => void
) {
  setTimeout(() => {
    if (window?.NYKAA_WEB_INTERFACE?.autoFillSubmit) {
      window.NYKAA_WEB_INTERFACE.autoFillSubmit();
    }
  }, 1000);

  if (!window?.grecaptcha) {
    reject(new Error(ERROR_FOR_CAPTCHA_NOT_LOADED));
  } else {
    resolve(CAPTCHA_LOADED);
  }
}

/**
 * Handles the error for captcha execution failure.
 * @param actionName - The name of the captcha action
 * @param locationSuffix - The location suffix (if any) for error logging
 * @param reject - The promise reject function
 */
function handleCaptchaExecutionError(
  actionName: string,
  locationSuffix: string,
  reject: (reason?: any) => void
) {
  const captchaExecutionError = new Error(ERROR_FOR_CAPTCHA_EXECUTION);
  logger.error(
    captchaExecutionError,
    `Error for: ${actionName}${locationSuffix}`
  );
  reject(captchaExecutionError);
}

/**
 * Handles the error when the captcha script fails to load.
 *
 * @param actionName - The name of the captcha action
 * @param locationSuffix - The location suffix (if any) for error logging
 * @param reject - The promise reject function
 */
function handleCaptchaLoadError(
  actionName: string,
  locationSuffix: string,
  reject: (reason?: any) => void
) {
  const captchaNotLoadedError = new Error(ERROR_FOR_CAPTCHA_NOT_LOADED);
  logger.error(
    captchaNotLoadedError,
    `Error for: ${actionName}${locationSuffix}`
  );
  reject(captchaNotLoadedError);
}

/**
 * Loads the reCAPTCHA script if it's not already loaded.
 * @returns A promise resolving when the script is loaded
 */
export function loadCaptcha(): Promise<string> {
  if (!API_KEY) {
    logger.error('API_KEY not found', 'Recaptcha key not found');
    return Promise.resolve('');
  }

  return new Promise((resolve, reject) => {
    try {
      // Check if grecaptcha is already loaded
      if (!window?.grecaptcha) {
        const recaptchaV3Script = createCaptchaScript();

        // Add event listeners for script load and error
        recaptchaV3Script.addEventListener('load', () =>
          handleScriptLoad(resolve, reject)
        );
        recaptchaV3Script.addEventListener('error', () =>
          reject(new Error(ERROR_FOR_CAPTCHA_NOT_LOADED))
        );

        // Append the script to the document head
        document.head.appendChild(recaptchaV3Script);
      }
    } catch (error) {
      logger.error(error, 'Error in loadCaptcha');
      reject(error);
    }
  });
}

const executeCaptchaScript = (
  resolve: (value: string) => void,
  reject: (reason?: any) => void,
  actionName: string,
  locationSuffix: string
) => {
  if (window.grecaptcha?.execute) {
    window.grecaptcha.execute(API_KEY, { action: actionName }).then(
      (token: string) => resolve(token),
      () => handleCaptchaExecutionError(actionName, locationSuffix, reject)
    );
  } else {
    handleCaptchaExecutionError(actionName, locationSuffix, reject);
  }
};

/**
 * Executes the reCAPTCHA process for a specific action.
 * @param actionName - Name of the action to execute for reCAPTCHA
 * @param meta - Optional metadata including location
 * @returns A promise resolving to the captcha token
 */
export const executeCaptcha = (
  actionName: string,
  meta?: MetaForCaptcha
): Promise<string> => {
  if (!API_KEY) {
    logger.error('API_KEY not found', 'Recaptcha key not found');
    return Promise.resolve('');
  }

  const locationSuffix = meta?.location ? ` @${meta.location}` : '';

  return new Promise((resolve, reject) => {
    try {
      if (window.grecaptcha) {
        // If reCAPTCHA is already loaded, execute it
        logger.error(`Executing reCaptcha GTM for action: ${actionName}`);
        executeCaptchaScript(resolve, reject, actionName, locationSuffix);
      } else {
        // Load reCAPTCHA script if it's not already loaded
        logger.error(
          `Loading and executing reCaptcha for action: ${actionName}`
        );
        loadCaptcha()
          .then(() =>
            setTimeout(() => {
              executeCaptchaScript(resolve, reject, actionName, locationSuffix);
            }, 1000)
          )
          .catch(() =>
            handleCaptchaLoadError(actionName, locationSuffix, reject)
          );
      }
    } catch (error) {
      logger.error(error, `Error for: ${actionName}${locationSuffix}`);
      reject(error);
    }
  });
};
